import React from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container
} from '@mui/material';
import {
  DesktopWindows as DesktopIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';

const DesktopOnlyMessage = () => {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#F9FBFD',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Paper
            elevation={0}
            sx={{
              background: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
            }}
          >
            {/* App Logo */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  margin: '0 auto',
                  mb: 2,
                }}
              >
                <img
                  src="/ck-auth-logo.png"
                  alt="CK Prism"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 700,
                  color: '#253E66',
                  mb: 0.5,
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                }}
              >
                CK Prism
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              >
                Secure Admin Portal
              </Typography>
            </Box>

            {/* Mobile Device Icon with Warning */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(231, 76, 60, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3,
                }}
              >
                <PhoneIcon sx={{ fontSize: 32, color: '#E74C3C' }} />
              </Box>
            </motion.div>

            {/* Main Message */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                color: '#253E66',
                mb: 1.5,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Desktop Access Required
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                mb: 3,
                lineHeight: 1.7,
                fontSize: '0.95rem',
              }}
            >
              CK Prism is designed for desktop and laptop computers only.
              The admin interface requires a larger screen for optimal security and usability.
            </Typography>

            {/* Instructions Card */}
            <Paper
              elevation={0}
              sx={{
                background: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                p: 2.5,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
                <ComputerIcon sx={{ color: '#4698D3', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  How to Access
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'left' }}>
                {[
                  'Open this application on a desktop computer or laptop',
                  'Use a modern web browser (Chrome, Firefox, Safari, or Edge)',
                  'Ensure your screen resolution is at least 1024px wide'
                ].map((instruction, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      mb: index < 2 ? 1.5 : 0
                    }}
                  >
                    <CheckIcon sx={{ color: '#27AE60', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontFamily: 'Metropolis, sans-serif',
                        lineHeight: 1.5,
                      }}
                    >
                      {instruction}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Action Button */}
            <Button
              variant="contained"
              onClick={handleRefresh}
              startIcon={<DesktopIcon />}
              fullWidth
              sx={{
                background: '#4698D3',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                py: 1.5,
                fontSize: '0.95rem',
                boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                '&:hover': {
                  background: '#3580B8',
                  boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                },
              }}
            >
              I'm on Desktop - Refresh Page
            </Button>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 2,
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
              }}
            >
              If you're already on a desktop, try refreshing the page or clearing your browser cache
            </Typography>

            {/* Footer */}
            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #E5E5E5' }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#BDBDBD',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                CK Prism - AWS SSO Management Platform
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default DesktopOnlyMessage;
